import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { FaUsers, FaGlobeAfrica, FaComments, FaUserTie } from 'react-icons/fa';
import { useApiData } from '../hooks/useApiData';
import { fetchAboutUs } from '../repository/api';

const AboutUsSection = () => {
  // Fetch about us data from API
  const { data: aboutData, loading, error } = useApiData(fetchAboutUs);

  // Default fallback data
  const defaultData = {
    opening_paragraph: "Founded in 2014 as a premium rate service provider (PRSP), Codewave Technologies Limited is a comprehensive ICT service provider registered with the communication Authority of Kenya (CAK). Since inception, we have grown our market niche in IT service delivery through provision of efficient, cost effective and reliable services to our clients. At Codewave Technologies Limited, we pride ourselves on our commitment to meeting the diverse needs of our clients. Whether it's telecommunications solutions, digital services, or IT infrastructure, we strive to provide innovative and tailored solutions that empower businesses to thrive in today's dynamic landscape. Our journey has been marked by continuous growth and evolution, driven by our unwavering dedication to excellence and customer satisfaction.",
    mission: "To deliver the highest Value-Add product design of Effective Information and entertainment solutions",
    vision: "To become the best VAS service provider around the globe.",
    clients_served: 500,
    countries: 5,
    messages_delivered: 1,
    team_members: 45
  };

  // Use API data or fallback to default
  const content = aboutData || defaultData;

  // Helper function to format numbers with k, M, B suffixes
  const formatNumber = (num) => {
    if (num >= 1000000000) {
      return { value: (num / 1000000000).toFixed(1), suffix: "B" };
    } else if (num >= 1000000) {
      return { value: (num / 1000000).toFixed(1), suffix: "M" };
    } else if (num >= 1000) {
      return { value: (num / 1000).toFixed(1), suffix: "K" };
    } else {
      return { value: num, suffix: "" };
    }
  };

  // Stats data - use API data or fallback
  const stats = [
    {
      icon: <FaUsers />,
      value: content.clients_served,
      suffix: "+",
      label: "Clients Served"
    },
    {
      icon: <FaGlobeAfrica />,
      value: content.countries,
      suffix: "",
      label: "Countries"
    },
    {
      icon: <FaComments />,
      value: content.messages_delivered,
      suffix: "",
      label: "Messages Delivered"
    },
    {
      icon: <FaUserTie />,
      value: content.team_members,
      suffix: "+",
      label: "Team Members"
    }
  ];

  // Show loading state while fetching data
  if (loading) {
    return (
      <Section id="about-section">
        <Container>
          <SectionHeader>
            <SectionTitle>About <HighlightText>Codewave</HighlightText></SectionTitle>
            <SectionDescription>
              Loading about section...
            </SectionDescription>
          </SectionHeader>
        </Container>
      </Section>
    );
  }

  // Show error state if API call failed
  if (error) {
    console.error('About section error:', error);
    // Continue with default data instead of showing error
  }

  return (
    <Section id="about-section">
      <Container>
        <SectionHeader>
          <SectionTitle>About <HighlightText>Codewave</HighlightText></SectionTitle>
          <SectionDescription>
            {content.opening_paragraph}
          </SectionDescription>
        </SectionHeader>

        <AboutContent>
          <MissionVisionColumn>
            <MissionBox>
              <MissionTitle>Our Mission</MissionTitle>
              <MissionText>
                {content.mission}
              </MissionText>
            </MissionBox>

            <VisionBox>
              <VisionTitle>Our Vision</VisionTitle>
              <VisionText>
                {content.vision}
              </VisionText>
            </VisionBox>
          </MissionVisionColumn>

            <StatsColumn>
              <StatsGrid>
                {stats.map((stat, index) => {
                  const formatted = formatNumber(stat.value);
                  return (
                    <StatBox key={index}>
                      <StatIcon>{stat.icon}</StatIcon>
                      <StatNumber>
                        <CountUp
                          start={0}
                          end={formatted.value}
                          duration={2}
                          delay={0.2 + index * 0.1}
                          isVisible={true}
                          suffix={formatted.suffix + stat.suffix}
                        />
                      </StatNumber>
                      <StatLabel>{stat.label}</StatLabel>
                    </StatBox>
                  );
                })}
              </StatsGrid>
            </StatsColumn>
        </AboutContent>
      </Container>
    </Section>
  );
};

// Counter Component for Stats Animation
const CountUp = ({ start, end, duration, delay, suffix, isVisible }) => {
  const [count, setCount] = useState(start);
  const countRef = useRef(start);
  const timeRef = useRef(null);

  useEffect(() => {
    if (!isVisible) return;

    const totalFrames = Math.round(duration * 60);
    const increment = (end - start) / totalFrames;

    let currentFrame = 0;

    // Clear any previous animation
    if (timeRef.current) clearTimeout(timeRef.current);

    // Delay the start of animation
    timeRef.current = setTimeout(() => {
      const timer = setInterval(() => {
        currentFrame++;
        countRef.current = countRef.current + increment;

        // Format the number appropriately - handle decimals
        let value = countRef.current;
        if (value >= end) {
          value = end;
          clearInterval(timer);
        }

        // Round to 1 decimal place if it's a decimal number
        if (value % 1 !== 0) {
          value = Math.round(value * 10) / 10;
        } else {
          value = Math.floor(value);
        }

        setCount(value);
      }, 1000 / 60); // 60fps

      return () => clearInterval(timer);
    }, delay * 1000);

    return () => {
      if (timeRef.current) clearTimeout(timeRef.current);
    };
  }, [start, end, duration, delay, isVisible]);

  return (
    <>{count}{suffix}</>
  );
};

export default AboutUsSection;

// === Animations ===
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const fadeInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const gradientAnimation = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

// === Styled Components ===
const Section = styled.section`
  padding: 100px 0;
  background: linear-gradient(135deg, #f8f9fb 0%, #f1f4f9 100%);
  position: relative;
  overflow: hidden;
  
  @media (max-width: 768px) {
    padding: 70px 0;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  z-index: 2;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 60px;
  
  @media (max-width: 768px) {
    margin-bottom: 40px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 42px;
  font-weight: 700;
  margin-bottom: 15px;
  color: #105090;
  
  @media (max-width: 768px) {
    font-size: 32px;
  }
  
  @media (max-width: 576px) {
    font-size: 28px;
  }
`;

const HighlightText = styled.span`
  background: linear-gradient(90deg, #105090, #2090B0);
  background-size: 200% auto;
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;
  animation: ${gradientAnimation} 4s ease infinite;
`;

const SectionDescription = styled.p`
  font-size: 18px;
  max-width: 700px;
  margin: 0 auto;
  color: #666;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const AboutContent = styled.div`
  display: flex;
  gap: 50px;
  align-items: center;
  
  @media (max-width: 992px) {
    flex-direction: column;
    gap: 60px;
  }
`;

const MissionVisionColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 30px;
  animation: ${fadeInLeft} 0.4s ease forwards;
`;

const StatsColumn = styled.div`
  flex: 1;
  animation: ${fadeInRight} 0.4s ease 0.1s forwards;
`;

const MissionBox = styled.div`
  background: white;
  padding: 40px;
  border-radius: 15px;
  box-shadow: 0 15px 30px rgba(0,0,0,0.05);
  border-left: 5px solid #105090;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 40px rgba(0,0,0,0.1);
  }
  
  @media (max-width: 576px) {
    padding: 30px;
  }
`;

const MissionTitle = styled.h3`
  color: #105090;
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 20px;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 50px;
    height: 3px;
    background: #105090;
    border-radius: 3px;
  }
`;

const MissionText = styled.p`
  color: #555;
  font-size: 16px;
  line-height: 1.7;
`;

const VisionBox = styled.div`
  background: white;
  padding: 40px;
  border-radius: 15px;
  box-shadow: 0 15px 30px rgba(0,0,0,0.05);
  border-left: 5px solid #2090B0;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 40px rgba(0,0,0,0.1);
  }
  
  @media (max-width: 576px) {
    padding: 30px;
  }
`;

const VisionTitle = styled.h3`
  color: #2090B0;
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 20px;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 50px;
    height: 3px;
    background: #2090B0;
    border-radius: 3px;
  }
`;

const VisionText = styled.p`
  color: #555;
  font-size: 16px;
  line-height: 1.7;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const StatBox = styled.div`
  background: white;
  padding: 30px;
  border-radius: 15px;
  text-align: center;
  box-shadow: 0 15px 30px rgba(0,0,0,0.05);
  transition: transform 0.3s ease;
  animation: ${fadeInUp} 0.3s ease forwards;
  animation-delay: ${props => props.delay + 0.1}s;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 40px rgba(0,0,0,0.1);
  }
`;

const StatIcon = styled.div`
  color: #2090B0;
  font-size: 30px;
  margin-bottom: 15px;
`;

const StatNumber = styled.div`
  font-size: 36px;
  font-weight: 700;
  margin-bottom: 10px;
  color: #105090;
  
  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

const StatLabel = styled.div`
  color: #666;
  font-size: 15px;
  font-weight: 500;
`;